import React, { useState } from "react";
import { usePipeline } from "../hooks/usePipeline";
import { GlossaryTerm } from "../types";
import { API_BASE_URL } from "../services/api";

interface ManualPageProps {
  onGoToExport?: (filename: string, thread_id: string) => void;
}

const ManualPage: React.FC<ManualPageProps> = ({ onGoToExport }) => {
  const {
    status, result, error,
    handleUpload, handleSkip, handleFeedback, reset,
  } = usePipeline();

  const [feedback, setFeedback]         = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const onFeedbackSubmit = async () => {
    await handleFeedback(feedback);
    setFeedback("");
    setFeedbackSent(true);
  };

  const getFilename = () =>
    (result as any)?.filename || (result as any)?.file_name;

  const handleDownload = async () => {
    const thread_id = (result as any)?.thread_id;
    const filename = getFilename();
    if (!thread_id) { alert("Session not found. Please re-upload the file."); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/batch/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ thread_id, output_format: "general", file_type: "excel" }),
      });
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]+)\1/);
      const serverFilename = match ? match[2].trim() : (filename || "download").replace(/\.[^.]+$/, "") + ".xlsx";
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = serverFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed. Please try again.");
    }
  };

  const handleExport = () => {
    const filename = getFilename();
    const thread_id = (result as any)?.thread_id;
    if (!filename || !thread_id) { alert("Session not found. Please re-upload."); return; }
    if (onGoToExport) onGoToExport(filename, thread_id);
  };

  // ...existing code...
  return (
    <div style={styles.container}>
      <h2 style={styles.title}>📄 Manual File Upload</h2>

      {status === "idle" && (
        <div style={styles.uploadBox}>
          <p style={styles.uploadText}>Select a file to catalog</p>
          <input type="file" onChange={onFileChange} style={styles.fileInput} />
        </div>
      )}

      {status === "uploading" && (
        <div style={styles.infoBox}>
          <p>⏳ Uploading and processing your file...</p>
        </div>
      )}

      {status === "processing" && result && (
        <div style={styles.resultBox}>
          <h3>📋 Review Extracted Metadata</h3>
          <div style={styles.metaRow}>
            <span style={styles.label}>File Name:</span>
            <span>{result.file_name || (result as any)?.filename}</span>
          </div>
          <div style={styles.metaRow}>
            <span style={styles.label}>Category:</span>
            <span style={styles.badge}>{result.metadata?.category}</span>
          </div>
          <div style={styles.metaRow}>
            <span style={styles.label}>Summary:</span>
            <span>{result.metadata?.summary}</span>
          </div>

            {result.metadata?.glossary_terms &&
            result.metadata.glossary_terms.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4>📚 Glossary Terms</h4>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={styles.th}>Term</th>
                      <th style={styles.th}>Definition</th>
                      <th style={styles.th}>Parent Glossary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.metadata?.glossary_terms ?? []).map((term: GlossaryTerm, i: number) => (
                      <tr key={i} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                        <td style={styles.td}>{term.term}</td>
                        <td style={styles.td}>{term.definition}</td>
                        <td style={styles.td}>{term.parent_glossary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          <div style={{ marginTop: 20 }}>
            <h4>💬 Provide Feedback (optional)</h4>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Enter your feedback here..."
              style={styles.textarea}
            />
            <button onClick={onFeedbackSubmit} style={styles.feedbackBtn} disabled={feedbackSent}>
              {feedbackSent ? "✅ Feedback Sent" : "Submit Feedback"}
            </button>
          </div>

          {/* ── Action Buttons ── */}
          <div style={styles.actions}>
            <button style={styles.downloadBtn} onClick={handleDownload}>
              📥 Download Excel
            </button>
            {/* NEW Export Button */}
            <button style={styles.exportBtn} onClick={handleExport} disabled>
              🗂️ Catalogue in Tool
            </button>
            <button style={styles.skipBtn} onClick={handleSkip}>
              ⏭ Skip This File
            </button>
          </div>
        </div>
      )}

      {status === "error" && (
        <div style={styles.errorBox}>
          <p>❌ {error}</p>
          <button style={styles.skipBtn} onClick={reset}>Try Again</button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container:   { maxWidth: 900, margin: "0 auto", padding: 32 },
  title:       { fontSize: 24, marginBottom: 24, color: "#333" },
  uploadBox:   { border: "2px dashed #aaa", borderRadius: 10, padding: 40, textAlign: "center", background: "#fafafa" },
  uploadText:  { fontSize: 16, color: "#666", marginBottom: 12 },
  fileInput:   { marginTop: 8 },
  infoBox:     { padding: 20, background: "#fff8e1", borderRadius: 8, color: "#856404" },
  resultBox:   { background: "#fff", border: "1px solid #ddd", borderRadius: 10, padding: 24 },
  metaRow:     { display: "flex", gap: 12, marginBottom: 10, alignItems: "flex-start" },
  label:       { fontWeight: "bold", minWidth: 120, color: "#555" },
  badge:       { background: "#e3f2fd", color: "#1565c0", padding: "2px 10px", borderRadius: 12, fontWeight: "bold" },
  table:       { width: "100%", borderCollapse: "collapse", marginTop: 8 },
  tableHeader: { background: "#1565c0", color: "#fff" },
  th:          { padding: "10px 14px", textAlign: "left", fontSize: 14 },
  td:          { padding: "10px 14px", fontSize: 14 },
  rowEven:     { background: "#f9f9f9" },
  rowOdd:      { background: "#fff" },
  textarea:    { width: "100%", height: 80, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, marginTop: 8 },
  feedbackBtn: { marginTop: 8, padding: "8px 16px", background: "#607d8b", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" },
  actions:     { display: "flex", gap: 16, marginTop: 24, flexWrap: "wrap" },
  downloadBtn: { padding: "12px 24px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15 },
  exportBtn:   { padding: "12px 24px", background: "#9e9e9e", color: "#fff", border: "none", borderRadius: 8, cursor: "not-allowed", fontSize: 15, opacity: 0.7 },
  skipBtn:     { padding: "12px 24px", background: "#f57c00", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15 },
  errorBox:    { background: "#ffebee", border: "1px solid #ef9a9a", borderRadius: 10, padding: 24 },
};

export default ManualPage;